import type {
  IncidentNormalized,
  IncidentHistoryNormalized,
  Service,
} from '@incidents/contracts';
import { useI18n } from '../i18n.tsx';
import { formatLocalFromDB, formatLocalHM } from '../dates.ts';
import type { SlotInfo } from './Timeline.tsx';

interface Props {
  service: Service;
  incidents: IncidentNormalized[];
  history: IncidentHistoryNormalized[];
  selectedSlot: SlotInfo | null;
  onClose: () => void;
}

export function ServiceDetailsInline({
  incidents,
  history,
  selectedSlot,
  onClose,
}: Props) {
  const { t, lang } = useI18n();
  const statusLabel = selectedSlot
    ? selectedSlot.status === 500
      ? t('legend.incident')
      : selectedSlot.status === 200
      ? t('legend.ok')
      : t('legend.none')
    : null;
  const activeIncidents = incidents.filter(
    (i) => !i.resolved_at && (i.status ?? '').toLowerCase() !== 'resolved',
  );

  return (
    <div className="mt-3 space-y-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-700">{t('ui.details', 'Details')}</h3>
        <button
          className="text-xs px-2 py-1 rounded bg-slate-200 hover:bg-slate-300"
          onClick={onClose}
        >
          {t('ui.close', 'Close')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-4">
        <h4 className="font-semibold mb-2">
          {t('ui.activeIncidents', 'Active incidents')}
        </h4>
        {activeIncidents.length === 0 ? (
          <div className="text-slate-400 text-sm">{t('ui.none', 'None')}</div>
        ) : (
          <div className="divide-y">
            {activeIncidents.map((i) => (
              <div
                key={i.id}
                className="py-2 flex items-start justify-between gap-4"
              >
                <div>
                  <div className="font-medium">{i.title}</div>
                  <div className="text-xs text-slate-600">
                    Début: {formatLocalFromDB(i.started_at_norm_utc ?? i.started_at ?? null, lang)}
                  </div>
                  {i.description ? (
                    <div className="text-xs text-slate-500 mt-1">{i.description}</div>
                  ) : null}
                </div>
                <span className="text-[10px] uppercase px-2 py-1 rounded bg-rose-100 text-rose-700 h-fit">
                  {i.severity || 'low'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedSlot ? (
        <div className="bg-white rounded-xl shadow p-4">
          <div className="text-sm">
            <div className="font-medium mb-1">{t('ui.slot', 'Selected slot')}</div>
            <div className="text-slate-600">
              {statusLabel} • {formatLocalHM(selectedSlot.slotStart, lang)} →{' '}
              {formatLocalHM(selectedSlot.slotEnd, lang)}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-4 text-slate-500 text-sm">
          Clique une cellule pour voir le détail (Incident / OK / No status yet).
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-4">
        <h4 className="font-semibold mb-2">{t('ui.past7', 'Past incidents (7d)')}</h4>
        {history.length === 0 ? (
          <div className="text-slate-400 text-sm">{t('ui.none', 'None')}</div>
        ) : (
          <div className="divide-y">
            {history.map((i) => (
              <div key={i.id} className="py-2 flex items-center justify-between">
                <div>
                  <div className="font-medium">{i.title}</div>
                  <div className="text-xs text-slate-500">
                    {formatLocalFromDB(i.started_at_norm_utc ?? i.started_at, lang)} →{' '}
                    {i.resolved_at || i.resolved_at_norm_utc
                      ? formatLocalFromDB(i.resolved_at_norm_utc ?? i.resolved_at, lang)
                      : '—'}
                  </div>
                </div>
                <div className="text-xs text-slate-600">{i.status}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
